<!-- Radar — which way the band is moving relative to where you are.

     THIS IS NOT RADAR IMAGERY, AND IT DOES NOT PRETEND TO BE. The maps addon
     ships no tiles on purpose: every provider worth using needs an account, a
     key and a bill, and baking one in would spend someone's money for them. So
     rather than an empty map or a fake one, this draws what the app actually
     knows — the hourly forecast, as a plan view.

     The rings are TIME, not distance. Labelling them in kilometres would be a
     claim about where the band is, and nothing here knows that; labelling them
     in hours is a claim about when it arrives, which is exactly what the
     forecast says. The band sits upwind, because that is the direction weather
     comes from, and the arrow points at you. -->
<template>
  <IonPage>
    <IonContent :fullscreen="true" class="radar">
      <div class="sheet">
        <header class="head">
          <h1>Radar</h1>
          <p class="meta">{{ place ? place.name : 'No place set' }}</p>
        </header>

        <svg class="plan" viewBox="0 0 320 268" role="img" :aria-label="summary">
          <rect class="paper" x="0" y="0" width="320" height="268" />

          <g class="grid">
            <circle v-for="r in RINGS" :key="r.r" :cx="CX" :cy="CY" :r="r.r" />
            <line :x1="CX" :y1="CY - 116" :x2="CX" :y2="CY + 116" />
            <line :x1="CX - 116" :y1="CY" :x2="CX + 116" :y2="CY" />
          </g>

          <g class="ring-labels">
            <text v-for="r in RINGS" :key="'l' + r.r" :x="CX + 4" :y="CY - r.r - 4">{{ r.label }}</text>
          </g>

          <g class="compass">
            <text :x="CX" :y="CY - 122" text-anchor="middle">N</text>
            <text :x="CX + 122" :y="CY + 4" text-anchor="middle">E</text>
            <text :x="CX" :y="CY + 130" text-anchor="middle">S</text>
            <text :x="CX - 122" :y="CY + 4" text-anchor="middle">W</text>
          </g>

          <g v-if="band" class="band">
            <line
              class="front"
              :x1="band.x1" :y1="band.y1" :x2="band.x2" :y2="band.y2"
              :stroke-opacity="band.alpha"
            />
            <line class="drift" :x1="band.ax1" :y1="band.ay1" :x2="band.ax2" :y2="band.ay2" />
            <polygon class="drift-head" :points="band.head" />
            <text class="eta" :x="band.lx" :y="band.ly" :text-anchor="band.anchor">{{ band.label }}</text>
          </g>

          <circle class="you" :cx="CX" :cy="CY" r="4" />
          <text class="you-label" :x="CX" :y="CY + 18" text-anchor="middle">you</text>

          <text v-if="!band" class="absent" :x="CX" :y="CY - 30" text-anchor="middle">
            {{ place ? 'No band in range' : 'Not reporting' }}
          </text>
        </svg>

        <p class="caption" :class="{ warn: !place }">{{ caption }}</p>

        <div class="actions">
          <button
            v-if="place"
            class="act"
            type="button"
            @click="openInMaps({ lat: place.latitude, lng: place.longitude, label: place.name })"
          >
            Open in Maps
          </button>
          <button class="act quiet" type="button" @click="router.push({ name: 'now' })">
            Back to now
          </button>
        </div>

        <p class="source">
          Drawn from the hourly forecast, not from radar imagery. Overhead has no
          tile provider connected.
        </p>
      </div>
    </IonContent>
  </IonPage>
</template>

<script setup>
import { computed } from 'vue';
import { IonPage, IonContent } from '@ionic/vue';
import { useRouter } from 'vue-router';
import '../chrome.js';
import { openInMaps } from '../../addons/maps/index.js';
import {
  place, observation, traceHours, thresholdMm, bearingOf, clockShort,
} from '../weather.js';

const router = useRouter();

const CX = 160;
const CY = 130;
const RINGS = [
  { r: 36, label: 'in 2h' },
  { r: 76, label: 'in 6h' },
  { r: 116, label: 'in 12h' },
];

/** The first hour that counts as rain, which is the one worth a distance. */
const arriving = computed(
  () => traceHours.value.find((h) => (Number(h.mm) || 0) >= thresholdMm.value) ?? null,
);

const peakMm = computed(
  () => traceHours.value.reduce((m, h) => Math.max(m, Number(h.mm) || 0), 0),
);

const band = computed(() => {
  if (!place.value || !arriving.value) return null;

  const hoursAway = traceHours.value.indexOf(arriving.value);
  const r = 36 + (Math.min(hoursAway, 12) / 12) * 80;

  // Bearing the wind comes FROM, which is where the band is. 0 is north, and
  // north is up, hence the sin/-cos rather than the usual cos/sin.
  const deg = bearingOf(observation.value?.windDirection);
  const t = (deg * Math.PI) / 180;
  const ux = Math.sin(t);
  const uy = -Math.cos(t);
  const px = -uy;             // unit vector along the front, perpendicular to u
  const py = ux;

  const bx = CX + r * ux;
  const by = CY + r * uy;
  const half = 58;

  // The drift arrow: from the front, toward you.
  const ax1 = bx - ux * 8;
  const ay1 = by - uy * 8;
  const ax2 = bx - ux * 30;
  const ay2 = by - uy * 30;
  const head = [
    `${bx - ux * 38},${by - uy * 38}`,
    `${ax2 + px * 5},${ay2 + py * 5}`,
    `${ax2 - px * 5},${ay2 - py * 5}`,
  ].join(' ');

  return {
    x1: bx - px * half, y1: by - py * half,
    x2: bx + px * half, y2: by + py * half,
    ax1, ay1, ax2, ay2, head,
    alpha: Math.min(0.85, 0.3 + peakMm.value * 0.08),
    lx: bx + px * (half + 6),
    ly: by + py * (half + 6) - 6,
    anchor: px < -0.2 ? 'end' : 'start',
    label: hoursAway === 0 ? 'now' : clockShort(arriving.value.time),
  };
});

const caption = computed(() => {
  if (!place.value) return 'Radar is not reporting for this area right now.';
  if (!traceHours.value.length) return 'Radar is not reporting for this area right now.';
  if (!arriving.value) return 'Nothing above your threshold in the next twelve hours.';
  const dir = String(observation.value?.windDirection || '').toLowerCase();
  const when = traceHours.value.indexOf(arriving.value);
  return `Band from the ${dir || 'west'}, ${when === 0 ? 'overhead now' : `arriving about ${clockShort(arriving.value.time)}`}.`;
});

// Everything the picture says, said again in words.
const summary = computed(() => (band.value
  ? `Plan view. You are at the centre; the rain band lies to the ${String(observation.value?.windDirection || '').toLowerCase()} and is expected ${band.value.label === 'now' ? 'now' : `about ${band.value.label}`}.`
  : 'Plan view with no rain band in range.'));
</script>

<style scoped>
.radar { --background: var(--oh-paper, #dfe3d1); }
.sheet {
  --paper: var(--oh-paper, #dfe3d1);
  --grid: var(--oh-grid, #b0522f);
  --ink: var(--oh-ink, #1b2540);
  --rain: var(--oh-rain, #35689f);
  --signal: var(--oh-signal, #a8331a);
  background: var(--paper);
  min-height: 100%;
}
.head { padding: env(safe-area-inset-top, 0px) 14px 0; }
.head h1 {
  margin: 16px 0 0;
  font-family: var(--oh-serif, Georgia, serif);
  font-style: italic;
  font-weight: 400;
  font-size: 27px;
  line-height: 1.1;
  color: var(--ink);
}
.meta,
.caption,
.source {
  font-family: var(--oh-mono, ui-monospace, monospace);
  letter-spacing: 0.06em;
  color: color-mix(in srgb, var(--ink) 60%, transparent);
}
.meta { margin: 3px 0 0; font-size: 11px; }
.plan { display: block; width: 100%; margin-top: 6px; }
.paper { fill: var(--paper); }
.grid circle { fill: none; stroke: var(--grid); stroke-opacity: 0.22; }
.grid line { stroke: var(--grid); stroke-opacity: 0.16; }
text {
  font-family: var(--oh-mono, ui-monospace, monospace);
  font-size: 10px;
  fill: var(--ink);
}
.ring-labels text,
.compass text { fill-opacity: 0.5; }
.you { fill: var(--ink); }
.you-label { fill-opacity: 0.6; }
.band .front { stroke: var(--rain); stroke-width: 7; stroke-linecap: round; }
.band .drift { stroke: var(--ink); stroke-width: 1.5; }
.band .drift-head { fill: var(--ink); }
.band .eta { fill: var(--signal); font-size: 11px; }
.absent { fill-opacity: 0.6; font-size: 12px; }
.caption { margin: 6px 14px 0; font-size: 11px; }
.caption.warn { color: var(--signal); }
.actions { display: flex; gap: 8px; padding: 16px 14px 18px; }
.act {
  min-height: 44px;
  padding: 0 16px;
  border: 1px solid color-mix(in srgb, var(--ink) 35%, transparent);
  border-radius: 2px;
  background: transparent;
  color: var(--ink);
  font-family: var(--oh-mono, ui-monospace, monospace);
  font-size: 12px;
  letter-spacing: 0.06em;
  cursor: pointer;
}
.act:focus-visible { outline: 2px solid var(--ink); outline-offset: 2px; }
.act.quiet { border-color: transparent; opacity: 0.65; }
.source {
  margin: 0;
  padding: 0 14px calc(20px + env(safe-area-inset-bottom, 0px));
  font-size: 10px;
  line-height: 1.6;
  color: color-mix(in srgb, var(--ink) 45%, transparent);
}
</style>
